import { useCallback, useEffect, useState } from 'react'
import { AddHouseholdScreen } from './AddHouseholdScreen'
import { HouseholdScreen } from './HouseholdScreen'
import { ReportScreen } from './ReportScreen'
import { SyncBar } from '../components/SyncBar'
import { localHouseholds, type LocalHousehold } from '../lib/db'
import { onOutboxChange, refreshHouseholds } from '../lib/sync'
import { t } from '../lib/i18n'
import { foldName } from '../lib/duplicates'

type Household = LocalHousehold

export function VillageScreen({
  villageId,
  villageName,
  villageCode,
  estimatedHouseholds,
  roleCode,
  onBack,
}: {
  villageId: string
  villageName: string
  villageCode: string
  estimatedHouseholds: number | null
  roleCode: string | null
  onBack: () => void
}) {
  const [rows, setRows] = useState<Household[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [openUid, setOpenUid] = useState<string | null>(null)
  const [justSaved, setJustSaved] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [showReport, setShowReport] = useState(false)

  // READ FROM THE PHONE, ALWAYS. The server refresh happens alongside and
  // simply updates the same local store, so there is no "online version" of
  // this screen that could behave differently from the one used in the field.
  const load = useCallback(async () => {
    setRows(await localHouseholds(villageId))
    setLoading(false)
  }, [villageId])

  useEffect(() => {
    load()
    // A refresh that cannot reach the server is not an error — it is Tuesday.
    refreshHouseholds(villageId)
      .then((ok) => { if (ok) load() })
      .catch(() => setError(null))
    return onOutboxChange(load)
  }, [load, villageId])

  if (showReport) {
    return (
      <ReportScreen
        villageId={villageId}
        villageName={villageName}
        villageCode={villageCode}
        roleCode={roleCode}
        onBack={() => setShowReport(false)}
      />
    )
  }

  if (openUid) {
    return (
      <HouseholdScreen
        householdUid={openUid}
        villageName={villageName}
        onBack={(changed) => {
          setOpenUid(null)
          // Only reload if something was actually saved. Reloading on every
          // Back would make the list flicker on a slow connection for nothing.
          if (changed) load()
        }}
      />
    )
  }

  if (adding) {
    return (
      <AddHouseholdScreen
        villageId={villageId}
        villageName={villageName}
        onCancel={() => setAdding(false)}
        onDone={(code) => {
          setAdding(false)
          setJustSaved(code)
          load()
        }}
      />
    )
  }

  // ---------------------------------------------------------------------------
  //  SEARCH
  // ---------------------------------------------------------------------------
  //  Four households fit on a screen. Four hundred do not, and an officer coming
  //  back to update a house they visited last week has no way to reach it.
  //
  //  Matching uses foldName() — the SAME folding the duplicate check and the
  //  database use. So "chan dara", "Chan  Dara" and "CHAN DARA" all find the
  //  same house, Khmer works, and an officer never has to wonder why search and
  //  the duplicate warning disagree about whether two names are the same.
  //
  //  Numbers are matched as a plain substring of the household code, so typing
  //  the "7" from a sticker finds KH-17090601-00007 without anyone having to
  //  learn the format. Entirely local, so it works with no signal.
  const q = foldName(query)
  const filtered = q === ''
    ? rows
    : rows.filter((h) => {
        if (h.head_name && foldName(h.head_name).includes(q)) return true
        const code = (h.household_code ?? '').toLowerCase()
        if (code.includes(q)) return true
        // Bare digits: strip the leading zeros people never type.
        if (/^\d+$/.test(q) && code.replace(/^.*-0*/, '').startsWith(q)) return true
        return false
      })

  const active = rows.filter((r) => r.record_status === 'active').length
  const coverage =
    estimatedHouseholds && estimatedHouseholds > 0
      ? Math.round((1000 * active) / estimatedHouseholds) / 10
      : null

  return (
    <div className="screen">
      <div className="appbar">
        <h1>{villageName}</h1>
        <button className="btn-quiet" onClick={onBack}>
          {t('common.back')}
        </button>
      </div>

      <SyncBar />

      <div className="content">
        <p className="muted" style={{ marginTop: 0 }}>
          {villageCode}
          {estimatedHouseholds != null && <> · {estimatedHouseholds} {t('villages.expected')}</>}
          {coverage != null && <> · {coverage}% {t('village.mapped')}</>}
        </p>

        {justSaved && (
          <div className="alert" style={{ borderColor: 'var(--green-700)', color: 'var(--green-900)', background: 'var(--green-100)' }}>
            <strong>{justSaved}</strong> — {t('village.savedLocal')}
          </div>
        )}

        <button className="btn-primary" onClick={() => setAdding(true)}>
          {t('village.add')}
        </button>

        {/* Below the primary action, not beside it. Adding a household is what
            an officer opened this screen to do; the summary is what they look
            at afterwards, and a second bright button next to the first one
            makes the day's work harder to start. */}
        <button
          className="btn-quiet"
          onClick={() => setShowReport(true)}
          style={{ marginTop: 10, minHeight: 44, width: '100%' }}
        >
          {t('village.report')}
        </button>

        {/* Only offered once the list is long enough to need it. A search box
            above four households is clutter that pushes the households down. */}
        {rows.length >= 8 && (
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('village.search')}
            aria-label={t('village.search')}
            autoCapitalize="none"
            autoCorrect="off"
            style={{ marginTop: 14 }}
          />
        )}

        {error && (
          <div className="alert alert-error" style={{ marginTop: 14 }}>
            {error}
          </div>
        )}

        {loading && <p className="spinner">{t('common.loading')}</p>}

        {!loading && q !== '' && filtered.length === 0 && (
          <p className="muted" style={{ marginTop: 16 }}>{t('village.searchNone')}</p>
        )}

        {!loading && q !== '' && filtered.length > 0 && (
          <p className="muted" style={{ marginTop: 12, marginBottom: 0 }}>
            {t('village.showing')} {filtered.length} {t('village.of')} {rows.length}
          </p>
        )}

        {!loading && rows.length === 0 && !error && (
          <p className="muted" style={{ marginTop: 20 }}>
            {t('village.empty')} <strong>KH-{villageCode}-00001</strong>
          </p>
        )}

        <ul className="list" style={{ marginTop: 16 }}>
          {filtered.map((h) => (
            <li key={h.household_uid} style={{ padding: 0 }}>
              {/* The whole row opens the household. A separate small "edit"
                  icon would be a 24px target on a phone held in one hand in
                  the sun — the row itself is the target. */}
              <button
                onClick={() => setOpenUid(h.household_uid)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  background: 'transparent',
                  border: 0,
                  padding: 14,
                }}
              >
                <div className="name">{h.head_name ?? t('village.noName')}</div>
                <div className="code">{h.household_code ?? t('village.noNumber')}</div>
                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {/* Not the same fact as "saved". The officer is told which. */}
                  {h.pending && <span className="pill pill-waiting">{t('sync.waiting')}</span>}
                  <span className="pill">{h.household_size} {t('common.people')}</span>
                  {h.has_latrine === true && <span className="pill">{t('village.hasLatrine')}</span>}
                  {h.has_latrine === false && <span className="pill">{t('village.noLatrine')}</span>}
                  {h.record_status !== 'active' && <span className="pill">{h.record_status}</span>}
                  {h.gps_accuracy_m != null && h.gps_accuracy_m > 25 && (
                    <span className="pill">{t('village.weakGps')} ±{Math.round(h.gps_accuracy_m)} m</span>
                  )}
                </div>
                <div className="muted" style={{ marginTop: 6, fontSize: 14 }}>
                  {t('village.tapHousehold')}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
